import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import NightlifeOutlinedIcon from '@mui/icons-material/NightlifeOutlined';
import PoolOutlinedIcon from '@mui/icons-material/PoolOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SportsBarOutlinedIcon from '@mui/icons-material/SportsBarOutlined';
import WineBarOutlinedIcon from '@mui/icons-material/WineBarOutlined';
import './App.css';
import LoginPage from './components/LoginPage';
import PartnerLayout from './components/partner/PartnerLayout';
import PartnerOverview from './components/partner/PartnerOverview';
import MyOffersPage from './components/partner/MyOffersPage';
import VenueGalleryPage from './components/partner/VenueGalleryPage';
import VenueProfilePage from './components/partner/VenueProfilePage';
import ClaimsPage from './components/partner/ClaimsPage';
import AnalyticsPage from './components/partner/AnalyticsPage';
import SettingsPage from './components/partner/SettingsPage';
import SupportPage from './components/partner/SupportPage';
import PoolPartiesPage from './components/partner/PoolPartiesPage';
import OfferAssistantPage from './components/partner/OfferAssistantPage';
import PartnerEventsPage from './components/partner/EventsPage';
import AgeGate from './components/legal/AgeGate';
import CookieConsent, { openCookieSettings } from './components/CookieConsent';
import { PrivacyContent, TermsContent } from './components/legal/LegalPages';
import { MyPoolPartyBookings, PoolPartiesList, PoolPartyDetails } from './features/poolParties/pages';
import { ComingSoon, publicValidity } from './components/partner/shared';
import VenueDetailsPage from './components/VenueDetailsPage';
import ClaimOfferPage from './components/ClaimOfferPage';
import AdminPage from './components/AdminPage';
import PartnerRegistrationPage from './components/PartnerRegistrationPage';
import FitImage from './components/FitImage';
import CreateInvitePage from './features/hangouts/pages/CreateInvitePage';
import InvitePreviewPage from './features/hangouts/pages/InvitePreviewPage';
import PublicInvitePage from './features/hangouts/pages/PublicInvitePage';
import InviteResponsesPage from './features/hangouts/pages/InviteResponsesPage';
import InviteChatPage from './features/hangouts/pages/InviteChatPage';
import MyInvitesPage from './features/hangouts/pages/MyInvitesPage';
import { NotificationBell } from './features/hangouts/components/NotificationBell';
import { useNearbyOffers, useRestaurants, useWineShops } from './hooks/useVenues';
import { AndroidAppInfo, androidApkUrl, getAndroidAppInfo, isCustomer, saveContact, submitPartnerApplication, venueImageUrl } from './api';
import AccountMenu from './components/AccountMenu';
import { initAnalytics, trackEvent, trackPageView } from './analytics';
import { isNativeApp, NativeBridge } from './native';
import LocationFilter from './components/LocationFilter';
import { useLocationFilter } from './hooks/useLocationFilter';
import { EventDetails, EventsList } from './features/events/pages';
import { mockCategoryFor } from './lib/mock';
import PosLayout from './features/pos/PosLayout';
import PosLogin from './features/pos/PosLogin';
import { FloorPage, OrdersPage } from './features/pos/FloorPage';
import OrderPage from './features/pos/OrderPage';
import { BillPrint, KotPrint } from './features/pos/PrintPages';
import { DayClosePage, ReportsPage } from './features/pos/ReportsPage';
import SetupPage from './features/pos/SetupPage';
import { categoryLabel, formatDays, formatDiscount, useFeatures } from './features';

const categories = [
  { label: 'Clubs', icon: <NightlifeOutlinedIcon />, to: '/explore' },
  { label: 'Pubs & Bars', icon: <SportsBarOutlinedIcon />, to: '/explore' },
  { label: 'Restaurants', icon: <RestaurantOutlinedIcon />, to: '/explore' },
  { label: 'Pool Parties', icon: <PoolOutlinedIcon />, to: '/pool-parties' },
  { label: 'Wine Shops', icon: <WineBarOutlinedIcon />, to: '/wine-shops', alcohol: true },
  { label: 'Events', icon: <CalendarMonthOutlinedIcon />, to: '/events' },
];
interface TrendingCard {
  key: string;
  name: string;
  area: string;
  badge: string;
  badgeClass: string;
  offer: string;
  meta: string;
  img: string;
  link: string;
  // Live offers beyond the one on the card, and where to see them all.
  moreOffers?: number;
  venueLink?: string;
}

const BADGE_CLASS: Record<string, string> = { Clubs: 'pink', 'Pubs & Bars': 'pink', Restaurants: 'gold-badge', Resorts: 'cyan' };
const FALLBACK_IMAGES = ['/hero.jpg', '/download.png'];
function Brand({ centered = false }: { centered?: boolean }) {
  return (
    <Link className={`logo ${centered ? 'centered' : ''}`} to="/">
      <img src="/logo2.png" alt="Cheers2Night" />
      <small>ROYAL PEOPLE ROYAL CHOICE</small>
    </Link>
  );
}

function Header() {
  const features = useFeatures();
  const [open, setOpen] = useState(false);
  // Re-read the session on every navigation, so the button updates right after login/logout.
  const { pathname } = useLocation();
  // Close the mobile menu once a link in it has been followed.
  useEffect(() => setOpen(false), [pathname]);
  // The partner portal is a full-screen app with its own sidebar and top bar.
  // The partner portal and the POS are full-screen apps with their own top bars.
  if (pathname.startsWith('/partner-account') || pathname.startsWith('/pos')) return null;
  return (
    <header className="header">
      <div className="header-inner">
        <Brand />
        <nav className={`nav ${open ? 'open' : ''}`}>
          <NavLink to="/">Home</NavLink>
          <NavLink to="/explore">Explore</NavLink>
          <NavLink to="/offers">Offers</NavLink>
          <NavLink to="/pool-parties">Pool Parties</NavLink>
          <NavLink to="/events">Events</NavLink>
          {features.alcoholOffers && <NavLink to="/wine-shops">Wine Shops</NavLink>}
          <NavLink to="/partner">Partner With Us</NavLink>
        </nav>
        <Link className="header-search" to="/explore" aria-label="Search"><SearchOutlinedIcon /></Link>
        {isCustomer() && <NotificationBell />}
        <AccountMenu />
        <button className="mobile menu-btn" onClick={() => setOpen(!open)} aria-label="Menu">Menu</button>
      </div>
    </header>
  );
}

// Sends a Google Analytics page view on every route change (no-op without a GA Measurement ID).
function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    initAnalytics();
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

// "Get the Android app" link, shown on the website once the pipeline has deployed an APK.
function AndroidAppLink() {
  const [app, setApp] = useState<AndroidAppInfo | null>(null);
  useEffect(() => {
    if (!isNativeApp) getAndroidAppInfo().then(setApp).catch(() => setApp(null));
  }, []);
  if (!app?.available) return null;
  return (
    <p>
      <a className="gold" href={androidApkUrl} download onClick={() => trackEvent('android_app_download', { version: app.versionName ?? undefined })}>
        Get the Android app{app.versionName ? ` (v${app.versionName})` : ''}
      </a>
    </p>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div><Brand /><p className="muted">Discover the best nightlife, restaurants, resorts, wine shops, events and exclusive offers.</p><AndroidAppLink /></div>
        <div><h4>Explore</h4><p><Link to="/explore">Venues</Link></p><p><Link to="/offers">Offers</Link></p><p><Link to="/pool-parties">Pool Parties</Link></p><p><Link to="/events">Events</Link></p></div>
        <div><h4>Company</h4><p><Link to="/about">About</Link></p><p><Link to="/contact">Contact</Link></p><p><Link to="/partner">Partner</Link></p><p><Link to="/terms">Terms of Use</Link></p><p><Link to="/privacy">Privacy Policy</Link></p><p><button type="button" className="link-button" onClick={openCookieSettings}>Cookie settings</button></p></div>
        <div><h4>Newsletter</h4><input className="input" placeholder="Your email" /><br /><br /><button className="btn btn-gold">Subscribe</button></div>
      </div>
    </footer>
  );
}

function PageHero({ title, subtitle, children }: { title: string; subtitle?: string; children?: React.ReactNode }) {
  return <section className="page-hero"><div className="container">{children}<h1>{title}</h1>{subtitle && <p>{subtitle}</p>}</div></section>;
}

// Venues are searched near the visitor's location (Pune when location access is denied).
function Search({ placeholder, value, onChange, onSubmit }: { placeholder: string; value: string; onChange: (value: string) => void; onSubmit?: () => void }) {
  const { area } = useLocationFilter();
  return (
    <form className="search" role="search" onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }}>
      <SearchOutlinedIcon className="search-leading" />
      <input placeholder={placeholder} aria-label={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      <span className="search-location"><LocationOnOutlinedIcon />{area.state ? area.district ?? area.state : 'Near you'}</span>
      <button className="btn btn-gold" type="submit">Search</button>
    </form>
  );
}

// value/onChange make it a working filter; without them the first chip just shows as selected.
function Filters({ items, value, onChange }: { items: string[]; value?: string; onChange?: (item: string) => void }) {
  const selected = value ?? items[0];
  return (
    <div className="filters">
      {items.map((item) => (
        <button type="button" className={`filter ${item === selected ? 'on' : ''}`} key={item} aria-pressed={item === selected} onClick={() => onChange?.(item)}>{item}</button>
      ))}
    </div>
  );
}

function StatusMessage({ status, empty, emptyText }: { status: 'loading' | 'success' | 'error'; empty: boolean; emptyText: string }) {
  if (status === 'loading') return <p className="muted">Loading...</p>;
  if (status === 'error') return <div className="panel"><p>Couldn't load this right now. Please refresh the page.</p></div>;
  return empty ? <div className="panel"><p className="muted">{emptyText}</p></div> : null;
}

const matches = (query: string, ...fields: (string | undefined)[]) => {
  const q = query.trim().toLowerCase();
  return !q || fields.some((f) => f?.toLowerCase().includes(q));
};

// Directions from the visitor's location to a venue, in Google Maps.
const directionsLink = (name: string, address: string) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${name}, ${address}`)}`;

// When an offer was last confirmed by the venue (its last edit in the partner portal).
const lastUpdated = (iso?: string) =>
  iso ? `Updated by venue ${new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : null;

// Live partner offers: text comes from the partner's "My Offers" and the picture is their offer
// photo, falling back to their cover photo, both managed from /partner-account.
function useTrendingCards(): TrendingCard[] {
  const { area } = useLocationFilter();
  const { data: restos } = useRestaurants(area);
  const live = restos
    .filter((r) => r.offer)
    .slice(0, 3)
    .map((r) => {
      const category = mockCategoryFor(r.id);
      return {
        key: `resto-${r.id}`,
        name: r.name,
        area: r.address,
        badge: category.toUpperCase(),
        badgeClass: BADGE_CLASS[category] ?? 'gold-badge',
        offer: r.offer!,
        meta: `Tonight - ${r.distanceKm} km away`,
        img: venueImageUrl(r.offerImageUrl) ?? venueImageUrl(r.filePath) ?? '/hero.jpg',
        link: `/venues/resto-${r.id}`,
        moreOffers: Math.max((r.liveOfferCount ?? 0) - 1, 0),
        venueLink: `/venues/resto-${r.id}`
      };
    });
  // Only real, venue-published offers: nothing is shown in place of them.
  return live;
}

// Nearest real venues (none are invented when there are no venues nearby).
function usePopularCards() {
  const { area } = useLocationFilter();
  const { data: restos } = useRestaurants(area);
  const real = restos.slice(0, 6).map((r, i) => ({
    key: `resto-${r.id}`,
    name: r.name,
    area: r.address,
    meta: `${r.distanceKm} km`,
    isDistance: true,
    img: venueImageUrl(r.filePath) ?? FALLBACK_IMAGES[i % FALLBACK_IMAGES.length],
    link: `/venues/resto-${r.id}`
  }));
  return real;
}

function Home() {
  const features = useFeatures();
  const { area } = useLocationFilter();
  const cards = useTrendingCards();
  const popular = usePopularCards();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  return (
    <>
      <section className="hero home-hero">
        <div className="container hero-inner">
          <div className="hero-copy">
            <div className="kicker">GOOD FOOD&nbsp;&nbsp; GREAT PEOPLE&nbsp;&nbsp; UNFORGETTABLE NIGHTS</div>
            <h1>Your Night.<br />Your City.<br /><span className="gold">Your Offers.</span></h1>
            <p>Discover restaurants, bars, pubs and cafés near you, with their latest offers, live music and events.</p>
          </div>
          <Search placeholder="Search venues, areas..." value={query} onChange={setQuery} onSubmit={() => { if (query.trim()) trackEvent('search', { search_term: query.trim(), source: 'home' }); navigate(`/explore${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`); }} />
          <div className="categories">
            {categories.filter((c) => !c.alcohol || features.alcoholOffers).map((item) => <Link className="category" to={item.to} key={item.label}><div className="circle">{item.icon}</div>{item.label}</Link>)}
          </div>
        </div>
      </section>
      <main className="container home-content">
        <section className="home-section">
          <div className="section-title"><div><h2>Trending Tonight</h2><span>The Latest Offers From Venues Near You.</span></div><Link className="gold" to="/offers">View All -&gt;</Link></div>
          {cards.length === 0 && <div className="panel"><p className="muted">No venue offers are live {area.state ? 'in this area' : 'near you'} right now. Explore venues, events and live music instead.</p></div>}
          <div className="home-grid">
            {cards.map((card) => (
              <article className="home-card" key={card.key}>
                <FitImage src={card.img} alt={card.name} />
                <div className={`home-badge ${card.badgeClass}`}>{card.badge}</div>
                <div className="home-card-info">
                  <h3>{card.name}</h3>
                  <p><LocationOnOutlinedIcon />{card.area}</p>
                  <b>{card.offer}{!!card.moreOffers && card.venueLink && <Link className="more-offers" to={card.venueLink}>+{card.moreOffers} more {card.moreOffers === 1 ? 'offer' : 'offers'}</Link>}</b>
                  <span>{card.meta}</span>
                  <Link className="btn btn-gold" to={card.link}>View Offer</Link>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="home-section popular-section">
          <div className="section-title"><div><h2>{area.state ? `Popular in ${area.district ?? area.state}` : 'Popular Near You'}</h2><span>{area.state ? 'Top Rated Venues in Your Chosen Area' : 'Top Rated Venues Near You'}</span></div><Link className="gold" to="/explore">View All -&gt;</Link></div>
          {popular.length === 0 && <div className="panel"><p className="muted">No venues are listed {area.state ? 'in this area' : 'near you'} yet.</p></div>}
          <div className="popular-row">
            {popular.map((card) => (
              <Link className={`popular-card ${card.isDistance ? 'has-distance' : ''}`} style={{ display: 'block' }} to={card.link} key={card.key}>
                <FitImage src={card.img} alt={card.name} />
                <div><h3>{card.name}</h3><p><LocationOnOutlinedIcon />{card.area}</p></div>
                <span className={card.isDistance ? 'popular-distance' : undefined}>{card.meta}</span>
              </Link>
            ))}
          </div>
        </section>
        <section className="exclusive-strip">
          <div><h2>Exclusive Offers</h2><p>Because Every Night Deserves More!</p></div>
          <Link className="btn btn-gold" to="/offers">View Offers -&gt;</Link>
        </section>
      </main>
    </>
  );
}

const EXPLORE_FILTERS = ['All', 'Venues', 'With Offers'];

function Explore() {
  const features = useFeatures();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const [filter, setFilter] = useState('All');
  const { area, label } = useLocationFilter();
  const { data: restos, status: restoStatus } = useRestaurants(area);
  const { data: shops, status: shopStatus } = useWineShops(area);
  const status = restoStatus === 'error' || shopStatus === 'error' ? 'error' : restoStatus === 'loading' || shopStatus === 'loading' ? 'loading' : 'success';

  const items = [
    ...restos.map((r, i) => ({ key: `resto-${r.id}`, name: r.name, address: r.address, distanceKm: r.distanceKm, offer: r.offer, moreOffers: Math.max((r.liveOfferCount ?? 0) - 1, 0), img: venueImageUrl(r.filePath) ?? FALLBACK_IMAGES[i % 2], badge: (r.liveOfferCount ?? 0) > 1 ? `${r.liveOfferCount} OFFERS` : r.offer ? 'OFFER TONIGHT' : 'VENUE', isShop: false })),
    ...shops.map((s) => ({ key: `shop-${s.id}`, name: s.name, address: s.address, distanceKm: s.distanceKm, offer: undefined, moreOffers: 0, img: '/download.png', badge: 'WINE SHOP', isShop: true }))
  ]
    .filter((v) => (filter === 'Venues' ? !v.isShop : filter === 'Wine Shops' ? v.isShop : filter === 'With Offers' ? !!v.offer : true))
    .filter((v) => matches(query, v.name, v.address, v.offer))
    .sort((a, b) => a.distanceKm - b.distanceKm);

  return (
    <>
      <PageHero title="Explore" subtitle="Find your perfect night near you." />
      <main className="container section">
        <Search placeholder="Search venues, areas..." value={query} onChange={setQuery} onSubmit={() => { if (query.trim()) trackEvent('search', { search_term: query.trim(), source: 'explore' }); setSearchParams(query.trim() ? { q: query.trim() } : {}); }} />
        <LocationFilter />
        {/* Wine shops are listed only while the Super Admin shows alcohol-related content. */}
        <Filters items={features.alcoholOffers ? [...EXPLORE_FILTERS, 'Wine Shops'] : EXPLORE_FILTERS} value={filter} onChange={setFilter} />
        <StatusMessage status={status} empty={items.length === 0} emptyText={query ? `No places match "${query}".` : area.state ? `No places listed in ${label} yet.` : 'No places found near you yet.'} />
        <div className="grid">
          {items.map((v) => (
            <article className="card" key={v.key}>
              <div className="card-image"><FitImage src={v.img} alt={v.name} /><span className={`badge ${v.offer ? 'pink' : ''}`}>{v.badge}</span></div>
              <div className="card-body">
                <h3>{v.name}</h3>
                <p className="muted">{v.address} · {v.distanceKm} km</p>
                {v.offer && <p><strong className="gold">{v.offer}</strong>{v.moreOffers > 0 && <Link className="more-offers" to={`/venues/${v.key}`}>+{v.moreOffers} more {v.moreOffers === 1 ? 'offer' : 'offers'}</Link>}</p>}
                <Link className="btn btn-dark" to={`/venues/${v.key}`}>View Details</Link>{' '}
                <a className="btn btn-gold" href={directionsLink(v.name, v.address)} target="_blank" rel="noreferrer" onClick={() => trackEvent('directions_click', { source: 'explore' })}>Directions</a>
              </div>
            </article>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}

function Offers() {
  const { area, label } = useLocationFilter();
  const { data, status } = useNearbyOffers(area);
  const [filter, setFilter] = useState('All');
  const features = useFeatures();
  // Filter chips are the categories actually live right now (food, live music, events, ...).
  const categories = Array.from(new Set(data.map((o) => categoryLabel(o.category))));
  const filters = ['All', ...(data.some((o) => o.isFeatured) ? ['Featured'] : []), ...categories];
  const shown = data.filter((o) => (filter === 'All' ? true : filter === 'Featured' ? o.isFeatured : categoryLabel(o.category) === filter));

  return (
    <>
      <PageHero title="Offers Near You" subtitle="Real offers, published by the venues themselves." />
      <main className="container section">
        <LocationFilter />
        {filters.length > 1 && <Filters items={filters} value={filter} onChange={setFilter} />}
        <StatusMessage status={status} empty={shown.length === 0} emptyText={`No offers are live ${area.state ? `in ${label}` : 'near you'} right now. Check back tonight!`} />
        <div className="grid">
          {shown.map((o, i) => (
            <article className="card" key={`${o.restoId}-${o.offerId ?? 'venue'}`}>
              <div className="card-image">
                <FitImage src={venueImageUrl(o.imageUrl) ?? FALLBACK_IMAGES[i % 2]} alt={o.title} />
                <span className="badge">{(formatDiscount(o.discountType, o.discountValue) ?? o.benefit ?? categoryLabel(o.category)).toUpperCase()}</span>
                {o.isFeatured && <span className="badge pink" style={{ left: 'auto', right: 10 }}>FEATURED</span>}
              </div>
              <div className="card-body">
                <h3>{o.title}</h3>
                {/* Every offer names the venue that created it. */}
                <p className="muted"><Link to={`/venues/resto-${o.restoId}`} style={{ color: 'inherit' }}>{o.venueName}</Link> · {o.address} · {o.distanceKm} km</p>
                {o.benefit && <p><strong className="gold">{o.benefit}</strong></p>}
                <p>{publicValidity(o)} · {formatDays(o.activeDays)}</p>
                {o.minimumSpend ? <p className="muted">Minimum bill ₹{o.minimumSpend.toLocaleString('en-IN')}</p> : null}
                {o.description && <p className="muted">{o.description.length > 110 ? `${o.description.slice(0, 110)}…` : o.description}</p>}
                {o.terms && <details><summary className="muted" style={{ fontSize: 12, cursor: 'pointer' }}>Terms & conditions</summary><p className="muted" style={{ fontSize: 12, whiteSpace: 'pre-line' }}>{o.terms}</p></details>}
                {lastUpdated(o.lastUpdatedUtc) && <p className="muted" style={{ fontSize: 12 }}>{lastUpdated(o.lastUpdatedUtc)}. Offer run by the venue.</p>}
                {features.offerClaims
                  ? <><Link className="btn btn-gold" to={`/offers/resto-${o.restoId}/claim${o.offerId ? `?offer=${o.offerId}` : ''}`}>Claim Offer</Link>{' '}</>
                  : null}
                {o.venuePhone && <><a className="btn btn-gold" href={`tel:${o.venuePhone.replace(/[^\d+]/g, '')}`} onClick={() => trackEvent('call_click', { source: 'offers' })}>Call Venue</a>{' '}</>}
                <a className="btn btn-dark" href={directionsLink(o.venueName, o.address)} target="_blank" rel="noreferrer" onClick={() => trackEvent('directions_click', { source: 'offers' })}>Directions</a>
              </div>
            </article>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}

// Partners list their events in the partner portal (Events); see features/events.
function Events() {
  return <><PageHero title="Upcoming Events" subtitle="Live Nights. Unforgettable Moments." /><EventsList /><Footer /></>;
}

function WineShops() {
  const features = useFeatures();
  const { area, label } = useLocationFilter();
  const { data, status } = useWineShops(area);
  const [query, setQuery] = useState('');
  const shown = data.filter((s) => matches(query, s.name, s.address));
  if (!features.alcoholOffers) {
    return <><PageHero title="Wine Shops" /><main className="container section"><div className="panel"><p className="muted">This section isn't available.</p><Link className="btn btn-gold" to="/explore">Explore venues</Link></div></main><Footer /></>;
  }
  return (
    <>
      <PageHero title="Local Wineshops" subtitle="Quality. Variety. Always Near You." />
      <main className="container section">
        <Search placeholder="Search wineshops, location..." value={query} onChange={setQuery} />
        <br />
        <LocationFilter />
        <StatusMessage status={status} empty={shown.length === 0} emptyText={query ? `No wine shops match "${query}".` : area.state ? `No wine shops listed in ${label} yet.` : 'No wine shops found near you yet.'} />
        {shown.map((s) => (
          <div className="list-card" key={s.id}>
            <img src="/download.png" alt="" />
            <div>
              <h3>{s.name}</h3>
              <p className="muted">{s.address}</p>
              <p>{s.distanceKm} km away{s.mobile1 ? <> · <a href={`tel:${s.mobile1}`}>{s.mobile1}</a></> : null}</p>
            </div>
            <div>
              <Link className="btn btn-dark" to={`/venues/shop-${s.id}`}>View Details</Link>{' '}
              <a className="btn btn-gold" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${s.name}, ${s.address}`)}`} target="_blank" rel="noreferrer">Directions</a>
            </div>
          </div>
        ))}
      </main>
      <Footer />
    </>
  );
}

type FormNotice = { ok: boolean; text: string } | null;

function FormMessage({ notice }: { notice: FormNotice }) {
  return notice ? <p role={notice.ok ? 'status' : 'alert'} className={notice.ok ? 'green' : 'gold'}><strong>{notice.text}</strong></p> : null;
}

const PARTNER_BENEFITS = [
  ['More Customers', 'Reach people actively looking for places to go tonight.'],
  ['Boost Revenue', 'Publish offers that turn nearby browsers into guests at your door.'],
  ['Business Tools', 'Manage offers, photos, events and venue details from your partner portal.']
];
const EMPTY_APPLICATION = { business: '', owner: '', mobile: '', email: '', city: '', state: '', pin: '', type: '', about: '' };

// Applications are emailed to the team and listed in the admin console (Data → Applications).
// Approving one creates the venue and emails the owner their login. Venues the team onboards by
// phone can instead get a registration code and sign up on /partner-register.
function Partner() {
  const [form, setForm] = useState(EMPTY_APPLICATION);
  const [notice, setNotice] = useState<FormNotice>(null);
  const [busy, setBusy] = useState(false);
  const field = (key: keyof typeof EMPTY_APPLICATION) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm({ ...form, [key]: e.target.value })
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await submitPartnerApplication({
        businessName: form.business.trim(),
        ownerName: form.owner.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        state: form.state.trim() || undefined,
        pinCode: form.pin.trim() || undefined,
        businessType: form.type || undefined,
        about: form.about.trim() || undefined
      });
      trackEvent('generate_lead', { form: 'partner_application', business_type: form.type || undefined, city: form.city.trim() || undefined });
      setForm(EMPTY_APPLICATION);
      setNotice({ ok: true, text: "Thanks! We've received your application and emailed you a confirmation. Our team will contact you shortly; once approved, you'll get your partner login by email." });
    } catch (err) {
      setNotice({ ok: false, text: (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Could not send your application. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero title="Partner With Cheers2Night" subtitle="Grow Your Business. Reach More People." />
      <main className="container section">
        <div className="grid">{PARTNER_BENEFITS.map(([title, body]) => <div className="panel" key={title}><h2 className="gold">{title}</h2><p>{body}</p></div>)}</div>
        <section className="section">
          <div className="panel">
            <h2>Already have a registration code?</h2>
            <p className="muted">If our team gave you a registration code, use it to create your partner login yourself. Otherwise just apply below: once approved, we'll email you your login.</p>
            <Link className="btn btn-gold" to="/partner-register">Register Your Venue</Link>{' '}
            <Link className="btn btn-dark" to="/login">Partner Login</Link>
          </div>
        </section>
        <div className="panel">
          <h2>Apply to Partner</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <input className="input" required placeholder="Business Name *" aria-label="Business Name" {...field('business')} />
              <input className="input" required placeholder="Owner / Contact Name *" aria-label="Owner / Contact Name" {...field('owner')} />
              <input className="input" required type="tel" placeholder="Mobile Number *" aria-label="Mobile Number" {...field('mobile')} />
              <input className="input" required type="email" placeholder="Email Address *" aria-label="Email Address" {...field('email')} />
              <input className="input" required placeholder="City *" aria-label="City" {...field('city')} />
              <input className="input" placeholder="State" aria-label="State" {...field('state')} />
              <input className="input" placeholder="PIN Code" aria-label="PIN Code" inputMode="numeric" {...field('pin')} />
              <select className="input" aria-label="Business Type" {...field('type')}>
                <option value="">Business Type</option><option>Club</option><option>Pub / Bar</option><option>Restaurant</option><option>Resort</option><option>Wine Shop</option>
              </select>
            </div>
            <br />
            <textarea className="input" placeholder="Tell us about your business" aria-label="Tell us about your business" {...field('about')} />
            <FormMessage notice={notice} />
            <br />
            <button className="btn btn-gold" type="submit" disabled={busy}>{busy ? 'Sending...' : 'Submit Application'}</button>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
}

// Founder's note, in Jitendra's own voice. Edit the text here; the page lays it out.
const FOUNDER = {
  name: 'Jitendra Verma',
  role: 'Founder & Owner, Cheers2Night',
  photo: '/jitendra-verma.jpg',
  why: [
    "Every weekend the same question came up with friends: where do we go tonight? The answer meant scrolling through a dozen apps and pages, calling venues, and still not knowing who had a good offer that night.",
    "At the same time, I saw great clubs, pubs, restaurants and wine shops struggle to reach people nearby. Their best offers were stuck on a poster at the door or buried in a social media post that nobody saw.",
    "I built Cheers2Night to bring the two together: one place where you can discover what's on near you tonight and find real offers in a few taps, and where venues can publish their offers, photos and events themselves and see who comes through the door.",
    "Royal People Royal Choice is the promise behind it: every guest deserves a great night out, and every good venue deserves to be found."
  ]
};

const ABOUT_PILLARS = [
  ['Our Mission', 'Make it effortless to find a great night out nearby, with real offers published by the venues themselves.'],
  ['Our Vision', "Become every city's go-to guide for nightlife, dining and celebrations, trusted by guests and venues alike."],
  ['Our Values', 'Honest offers, respect for our partners, and a community that celebrates responsibly.']
];

function About() {
  return (
    <>
      <PageHero title="About Cheers2Night" subtitle="Royal People Royal Choice" />
      <main className="container section">
        <h2>Discover. Explore. Celebrate.</h2>
        <p className="muted">Cheers2Night connects people with nightlife, restaurants, resorts, wine shops, events and exclusive local offers.</p>

        <section className="panel founder">
          <div className="founder-id">
            <img className="founder-avatar" src={FOUNDER.photo} alt={FOUNDER.name} width={160} height={160} />
            <div><h3>{FOUNDER.name}</h3><p className="gold">{FOUNDER.role}</p></div>
          </div>
          <div>
            <h2>Why I built Cheers2Night</h2>
            {FOUNDER.why.map((para) => <p key={para.slice(0, 24)}>{para}</p>)}
            <p className="founder-sign">— {FOUNDER.name}</p>
          </div>
        </section>

        <div className="grid section">{ABOUT_PILLARS.map(([title, body]) => <div className="panel" key={title}><h2 className="gold">{title}</h2><p>{body}</p></div>)}</div>
        <div className="panel" style={{ textAlign: 'center' }}>
          <h2>Own a venue?</h2>
          <p className="muted">Publish your offers, photos and events on Cheers2Night and reach people looking for a night out near you.</p>
          <Link className="btn btn-gold" to="/partner">Partner With Us</Link>
        </div>
      </main>
      <Footer />
    </>
  );
}

const EMPTY_MESSAGE = { name: '', email: '', subject: '', message: '' };

function Contact() {
  const [form, setForm] = useState(EMPTY_MESSAGE);
  const [notice, setNotice] = useState<FormNotice>(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await saveContact({
        firstName: form.name.trim(), lastName: '', city: '', email: form.email.trim(), phoneNumber: '',
        comments: form.subject.trim() ? `${form.subject.trim()}\n${form.message.trim()}` : form.message.trim()
      });
      trackEvent('contact_message', { has_subject: !!form.subject.trim() });
      setForm(EMPTY_MESSAGE);
      setNotice({ ok: true, text: "Thanks for reaching out! We'll get back to you soon." });
    } catch {
      setNotice({ ok: false, text: 'Could not send your message. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const set = (key: keyof typeof EMPTY_MESSAGE) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [key]: e.target.value });

  return <><PageHero title="Contact Us" subtitle="We'd Love to Hear From You" /><main className="container section"><div className="two-col"><form className="panel" onSubmit={handleSubmit}><h2>Send a Message</h2><input className="input" required placeholder="Name *" aria-label="Name" value={form.name} onChange={set('name')} /><br /><br /><input className="input" required type="email" placeholder="Email *" aria-label="Email" value={form.email} onChange={set('email')} /><br /><br /><input className="input" placeholder="Subject" aria-label="Subject" value={form.subject} onChange={set('subject')} /><br /><br /><textarea className="input" required placeholder="Message *" aria-label="Message" value={form.message} onChange={set('message')} /><FormMessage notice={notice} /><br /><button className="btn btn-gold" type="submit" disabled={busy}>{busy ? 'Sending...' : 'Send Message'}</button></form><div className="panel"><h2>Get In Touch</h2><p>Pune, Maharashtra, India</p><p>support@cheers2night.com</p><p>+91 98765 43210</p><h3>Social</h3><p>Instagram - Facebook - YouTube - LinkedIn</p></div></div></main><Footer /></>;
}

function Auth({ mode }: { mode: 'login' | 'register' | 'forgot' }) {
  const isLogin = mode === 'login';
  const isForgot = mode === 'forgot';
  return <main className="auth"><div className="auth-card"><Brand centered /><h2>{isForgot ? 'Forgot Password' : isLogin ? 'Login' : 'Create Account'}</h2>{isForgot && <p className="muted">Enter your account email and we'll send reset instructions.</p>}{!isLogin && !isForgot && <input className="input" placeholder="Full Name" />}<input className="input" placeholder={isLogin ? 'Email or Mobile' : 'Email Address'} />{!isForgot && <><input className="input" type="password" placeholder="Password" />{!isLogin && <input className="input" type="password" placeholder="Confirm Password" />}</>}{isLogin && <p><label><input type="checkbox" /> Remember Me</label><Link className="gold right" to="/forgot-password">Forgot Password?</Link></p>}<Link className="btn btn-gold full" to={isLogin ? '/dashboard' : '/login'}>{isForgot ? 'Send Reset Link' : isLogin ? 'Login' : 'Create Account'}</Link><p className="centered">{isLogin ? 'New here? ' : isForgot ? '' : 'Already registered? '}{isLogin && <Link className="gold" to="/register">Create Account</Link>}{!isLogin && <Link className="gold" to="/login">{isForgot ? '<- Back to Login' : 'Login'}</Link>}</p></div></main>;
}

// Claiming and booking are switched off (discovery-only); old links go to the venue or listing.
function ClaimRoute() {
  const features = useFeatures();
  const { slug } = useParams<{ slug: string }>();
  if (features.offerClaims) return <ClaimOfferPage />;
  return slug?.startsWith('resto-') || slug?.startsWith('shop-') ? <Navigate to={`/venues/${slug}`} replace /> : <Navigate to="/offers" replace />;
}

function BookingsRoute() {
  const features = useFeatures();
  return features.poolPartyBookings
    ? <><PageHero title="My Bookings" subtitle="Your pool-party spots and entry codes." /><MyPoolPartyBookings /><Footer /></>
    : <Navigate to="/pool-parties" replace />;
}

function PartnerClaimsRoute() {
  const features = useFeatures();
  return features.offerClaims ? <ClaimsPage /> : <Navigate to="/partner-account" replace />;
}

function App() {
  return (
    <Router>
      <AnalyticsTracker />
      <NativeBridge />
      <AgeGate />
      <CookieConsent />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<><EventDetails /><Footer /></>} />
        <Route path="/wine-shops" element={<WineShops />} />
        <Route path="/pool-parties" element={<><PageHero title="Pool Parties" subtitle="Sun, Splash & Good Vibes." /><PoolPartiesList /><Footer /></>} />
        <Route path="/pool-parties/:id" element={<><PoolPartyDetails /><Footer /></>} />
        <Route path="/profile/bookings" element={<BookingsRoute />} />
        <Route path="/terms" element={<><PageHero title="Terms of Use" /><TermsContent /><Footer /></>} />
        <Route path="/privacy" element={<><PageHero title="Privacy Policy" /><PrivacyContent /><Footer /></>} />
        <Route path="/venues/:slug" element={<VenueDetailsPage />} />
        <Route path="/offers/:slug/claim" element={<ClaimRoute />} />
        {/* Invite to Hangout */}
        <Route path="/venues/:venueId/invite" element={<CreateInvitePage />} />
        <Route path="/invites/:inviteId/edit" element={<CreateInvitePage />} />
        <Route path="/invites/:inviteId/preview" element={<InvitePreviewPage />} />
        <Route path="/invites/:inviteId/responses" element={<InviteResponsesPage />} />
        <Route path="/invites/:inviteId/chat" element={<InviteChatPage />} />
        <Route path="/invite/:publicToken" element={<PublicInvitePage />} />
        <Route path="/profile/invites" element={<MyInvitesPage />} />
        <Route path="/partner" element={<Partner />} />
        <Route path="/partner-register" element={<PartnerRegistrationPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Auth mode="register" />} />
        <Route path="/forgot-password" element={<Auth mode="forgot" />} />
        {/* Former mock dashboards (fake offers); customers browse without an account. */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/my-account" element={<Navigate to="/" replace />} />
        {/* Partner portal: where owners land after login. */}
        <Route path="/partner-account" element={<PartnerLayout />}>
          <Route index element={<PartnerOverview />} />
          {/* One page: the offer list, with the add/edit panel open for .../new and .../:id/edit. */}
          <Route path="offers" element={<MyOffersPage />} />
          <Route path="offers/new" element={<MyOffersPage />} />
          <Route path="offers/:id/edit" element={<MyOffersPage />} />
          <Route path="offer-assistant" element={<OfferAssistantPage />} />
          <Route path="venue" element={<VenueProfilePage />} />
          <Route path="gallery" element={<VenueGalleryPage />} />
          <Route path="claims" element={<PartnerClaimsRoute />} />
          <Route path="events" element={<PartnerEventsPage />} />
          <Route path="pool-parties" element={<PoolPartiesPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="reviews" element={<ComingSoon title="Reviews" />} />
          <Route path="payouts" element={<ComingSoon title="Payouts" />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="support" element={<SupportPage />} />
        </Route>
        <Route path="/admin" element={<AdminPage />} />
        {/* C2N POS: billing for partner venues (owner login or staff PIN). */}
        <Route path="/pos/login" element={<PosLogin />} />
        <Route path="/pos/print/kot" element={<KotPrint />} />
        <Route path="/pos/print/bill/:id" element={<BillPrint />} />
        <Route path="/pos" element={<PosLayout />}>
          <Route index element={<FloorPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="order/:id" element={<OrderPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="day-close" element={<DayClosePage />} />
          <Route path="setup" element={<SetupPage />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
